import { useTranslation } from "next-i18next";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { ICONS } from "@/components/icons";
import ArticleIcon from "@/components/icons/article-icon";
import Close from "@/components/icons/close-icon";

import { useIsArcade } from "@/hooks/useIsArcade";

import ArticleTile from "../modules/article-tile";
import styles from "./search-dialog.module.scss";

interface SearchResult {
    title: string;
    token: string;
    slug: string;
    description: string;
    url: string;
}

interface SearchDialogProps {
    isOpen: boolean;
    onClose: () => void;
    searchQuery: string;
    onSearchChange: (query: string) => void;
    apiEndpoint?: string;
    searchPagePath?: string;
}

const useDebouncedValue = (value: string, delayMs: number): string => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(id);
    }, [value, delayMs]);
    return debounced;
};

export const SearchDialog: React.FC<SearchDialogProps> = ({
    isOpen,
    onClose,
    searchQuery,
    onSearchChange,
    apiEndpoint = "/api/help/search",
    searchPagePath = "/help/search",
}) => {
    const { t } = useTranslation();
    const { locale } = useRouter();
    const isArcade = useIsArcade();
    const [results, setResults] = useState<SearchResult[] | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    const debouncedQuery = useDebouncedValue(searchQuery, 300);

    // Clear results immediately when user types so stale results don't linger
    useEffect(() => {
        setResults(undefined);
    }, [searchQuery]);

    useEffect(() => {
        if (!debouncedQuery) {
            setResults(undefined);
            setLoading(false);
            return;
        }

        const controller = new AbortController();

        const fetchResults = async () => {
            setLoading(true);
            setError(null);

            try {
                const separator = apiEndpoint.includes("?") ? "&" : "?";
                const response = await fetch(
                    `${apiEndpoint}${separator}q=${encodeURIComponent(debouncedQuery)}&locale=${
                        locale ?? "en-US"
                    }`,
                    { signal: controller.signal }
                );

                if (controller.signal.aborted) return;

                if (!response.ok) {
                    throw new Error("Failed to fetch search results");
                }

                const data = await response.json();
                if (!controller.signal.aborted) {
                    setResults(data.results || []);
                }
            } catch (err) {
                if (err instanceof DOMException && err.name === "AbortError") return;
                if (!controller.signal.aborted) {
                    setError(t("help.search.error"));
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchResults();

        return () => {
            controller.abort();
        };
    }, [debouncedQuery, apiEndpoint, t]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
        }

        return () => {
            document.removeEventListener("keydown", handleEscape);
        };
    }, [isOpen, onClose]);

    // Focus trap: keep focus within the modal when open
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key !== "Tab" || !modalRef.current) return;

            const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
            if (focusableElements.length === 0) return;

            const first = focusableElements[0];
            const last = focusableElements[focusableElements.length - 1];

            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        },
        []
    );

    if (!isOpen) return null;

    return (
        <>
            <div className={styles.overlay} onClick={onClose} />
            <div className={styles.modalContainer}>
                <div
                    className={styles.modal}
                    ref={modalRef}
                    role="dialog"
                    aria-modal="true"
                    aria-label={t("help.search.placeholder")}
                    onKeyDown={handleKeyDown}
                >
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label={t("help.close")}
                    >
                        <Close
                            color="#000000"
                            width={16}
                            height={16}
                            viewBoxWidth={24}
                            viewBoxHeight={24}
                        />
                    </button>

                    <div className={styles.searchHeader}>
                        <div className={styles.searchInputWrapper}>
                            <div className={styles.searchIconWrapper}>
                                <ICONS.Search color="rgb(153, 153, 153)" />
                            </div>
                            <input
                                className={styles.searchInput}
                                placeholder={t("help.search.placeholder")}
                                value={searchQuery}
                                onChange={(e) => onSearchChange(e.target.value)}
                                autoFocus
                                type="text"
                            />
                            {searchQuery && (
                                <button
                                    className={styles.clearButton}
                                    onClick={() => onSearchChange("")}
                                    aria-label={t("help.search.clearSearch")}
                                >
                                    <Close
                                        color="#666666"
                                        width={12}
                                        height={12}
                                        viewBoxWidth={24}
                                        viewBoxHeight={24}
                                    />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className={styles.resultsSection}>
                        {loading && (
                            <div className={styles.loadingState}>
                                <p>{t("help.search.loading")}</p>
                            </div>
                        )}

                        {!loading && error && (
                            <div className={styles.errorState}>
                                <p>{error}</p>
                            </div>
                        )}

                        {!loading && !error && searchQuery && results !== undefined && results.length === 0 && (
                            <div className={styles.emptyState}>
                                <p>{t("help.search.noResults", { query: searchQuery })}</p>
                            </div>
                        )}

                        {!loading && !error && results && results.length > 0 && (
                            <>
                                {isArcade ? (
                                    <h6 className={styles.resultsHeading}>
                                        {t("help.search.topArticles")}
                                    </h6>
                                ) : (
                                    <h2 className={styles.resultsHeading}>
                                        {t("help.search.topArticles")}
                                    </h2>
                                )}
                                <div className={styles.resultsList}>
                                    {results.map((result) => (
                                        <ArticleTile
                                            key={result.slug}
                                            title={result.title}
                                            titleClassName={styles.resultTitle}
                                            subtitle={<span>{result.description}</span>}
                                            onClick={onClose}
                                            href={result.url}
                                            leftIcon={
                                                <div className={styles.iconWrapper}>
                                                    <ArticleIcon color="#333333" />
                                                </div>
                                            }
                                            rightIcon={
                                                <div className={styles.iconWrapper}>
                                                    <ICONS.Push color="currentColor" />
                                                </div>
                                            }
                                        />
                                    ))}
                                </div>
                                <Link
                                    href={`${searchPagePath}#${encodeURIComponent(searchQuery)}`}
                                    className={styles.moreResultsLink}
                                    onClick={onClose}
                                >
                                    {t("help.search.moreResults")}
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
